<template>
    <div class="language-selector" ref="selectorRef">
        <!-- Current Language Display -->
        <button
            @click="toggleDropdown"
            class="language-selector__trigger"
            :class="{ 'language-selector__trigger--open': isOpen }"
        >
            <div class="language-selector__current">
                <span
                    class="language-selector__flag"
                    v-html="getCurrentFlag()"
                ></span>
                <span class="language-selector__label">{{
                    getCurrentLanguageName()
                }}</span>
                <i
                    class="fa-solid fa-chevron-down language-selector__chevron"
                    :class="{ 'language-selector__chevron--rotated': isOpen }"
                ></i>
            </div>
        </button>

        <!-- Dropdown Menu -->
        <Transition name="dropdown">
            <div
                v-if="isOpen"
                class="language-selector__dropdown language-selector__dropdown--upward"
            >
                <div
                    v-for="lang in availableLanguages"
                    :key="lang.code"
                    @click="selectLanguage(lang.code)"
                    class="language-selector__option"
                    :class="{
                        'language-selector__option--active':
                            lang.code === locale,
                    }"
                >
                    <span
                        class="language-selector__flag"
                        v-html="lang.flagSvg"
                    ></span>
                    <span class="language-selector__name">{{ lang.name }}</span>
                    <i
                        v-if="lang.code === locale"
                        class="fa-solid fa-check language-selector__check"
                    ></i>
                </div>
            </div>
        </Transition>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

const router = useRouter();
const { locale } = useI18n();

const isOpen = ref(false);
const selectorRef = ref(null);

// SVG flags
const flags = {
    fr: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 30" width="20" height="15">
        <rect x="0" y="0" width="20" height="30" fill="#002395"/>
        <rect x="20" y="0" width="20" height="30" fill="#ffffff"/>
        <rect x="40" y="0" width="20" height="30" fill="#ed2939"/>
    </svg>`,

    en: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 30" width="20" height="15">
        <clipPath id="t">
            <path d="M0,0 v30 h60 v-30 z" />
        </clipPath>
        <g clip-path="url(#t)">
            <path d="M0,0 v30 h60 v-30 z" fill="#012169"/>
            <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" stroke-width="6"/>
            <path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" stroke-width="4"/>
            <path d="M30,0 v30 M0,15 h60" stroke="#fff" stroke-width="10"/>
            <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" stroke-width="6"/>
        </g>
    </svg>`,

    nl: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 30" width="20" height="15">
        <rect x="0" y="0" width="60" height="10" fill="#ae1c28"/>
        <rect x="0" y="10" width="60" height="10" fill="#ffffff"/>
        <rect x="0" y="20" width="60" height="10" fill="#21468b"/>
    </svg>`,
};

// Available languages with SVG flags
const availableLanguages = [
    { code: 'fr', name: 'Français', flagSvg: flags.fr },
    { code: 'en', name: 'English', flagSvg: flags.en },
    { code: 'nl', name: 'Nederlands', flagSvg: flags.nl },
];

const getCurrentFlag = () => {
    const current = availableLanguages.find(lang => lang.code === locale.value);
    return current ? current.flagSvg : flags.fr;
};

const getCurrentLanguageName = () => {
    const current = availableLanguages.find(lang => lang.code === locale.value);
    return current ? current.name : 'Français';
};

const toggleDropdown = async () => {
    isOpen.value = !isOpen.value;
};

const selectLanguage = newLocale => {
    if (newLocale !== locale.value) {
        switchLanguage(newLocale);
    }
    isOpen.value = false;
};

const switchLanguage = newLocale => {
    const currentRoute = router.currentRoute.value;
    const name = currentRoute.name;
    const params = {
        ...currentRoute.params,
        lang: encodeURIComponent(newLocale),
    };
    const query = currentRoute.query;
    router.push({
        name,
        params,
        query,
        hash: '',
    });
};

// Close dropdown when clicking outside
const closeDropdown = event => {
    if (!event.target.closest('.language-selector')) {
        isOpen.value = false;
    }
};

// Recalculate position on scroll/resize

onMounted(() => {
    document.addEventListener('click', closeDropdown);
});

onUnmounted(() => {
    document.removeEventListener('click', closeDropdown);
});
</script>

<style lang="scss" scoped>
@use '@/scss/variables' as vars;

.language-selector {
    position: relative;
    display: inline-block;

    &__trigger {
        display: flex;
        align-items: center;
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 0.5rem;
        padding: 0.5rem 0.75rem;
        cursor: pointer;
        transition: all 0.2s ease;
        min-width: 140px;

        &:hover {
            border-color: #d1d5db;
            box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
        }

        &--open {
            border-color: #10b981;
            box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }
    }

    &__current {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        width: 100%;
    }

    &__flag {
        width: 20px;
        height: 15px;
        border-radius: 2px;
        overflow: hidden;
        border: 1px solid rgba(0, 0, 0, 0.1);
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;

        :deep(svg) {
            width: 100%;
            height: 100%;
            display: block;
        }
    }

    &__label {
        font-size: 0.875rem;
        font-weight: 500;
        color: #374151;
        flex: 1;
    }

    &__chevron {
        font-size: 0.75rem;
        color: #6b7280;
        transition: transform 0.2s ease;

        &--rotated {
            transform: rotate(180deg);
        }
    }

    &__dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 0.5rem;
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
            0 4px 6px -2px rgba(0, 0, 0, 0.05);
        z-index: 9999; // High z-index to appear above cookie banners
        overflow: hidden;
        margin-top: 0.25rem;

        // Upward direction variant
        &--upward {
            top: auto;
            bottom: 100%;
            margin-top: 0;
            margin-bottom: 0.25rem;
        }
    }

    &__option {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.75rem;
        cursor: pointer;
        transition: background-color 0.15s ease;

        &:hover {
            background-color: #f9fafb;
        }

        &--active {
            background-color: #ecfdf5;
            color: #10b981;
        }
    }

    &__name {
        font-size: 0.875rem;
        font-weight: 500;
        flex: 1;
    }

    &__check {
        font-size: 0.75rem;
        color: #10b981;
    }
}

// Dropdown animations
.dropdown-enter-active,
.dropdown-leave-active {
    transition: all 0.2s ease;
}

.dropdown-enter-from {
    opacity: 0;
    transform: translateY(-10px) scale(0.95);
}

.dropdown-leave-to {
    opacity: 0;
    transform: translateY(-10px) scale(0.95);
}

// Special animations for upward dropdown
.language-selector__dropdown--upward {
    &.dropdown-enter-from {
        transform: translateY(10px) scale(0.95);
    }

    &.dropdown-leave-to {
        transform: translateY(10px) scale(0.95);
    }
}

// Responsive design
@media (max-width: 640px) {
    .language-selector {
        &__trigger {
            min-width: 120px;
            padding: 0.375rem 0.5rem;
        }

        &__label {
            display: none; // Hide text on mobile, show only flag
        }

        &__name {
            font-size: 0.8rem;
        }
    }
}
</style>
